import React, { useState, useMemo } from "react";
import DownloadIcon from "@mui/icons-material/Download";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import SearchIcon from "@mui/icons-material/Search";
import {
  formatDateTimeView,
  formatDateType,
} from "../../../commons/FormatDate";
import ViewInjectDrawal from "../../Modals/ConGen/ViewInjectDrawal/ViewInjectDrawal";
import { SuccessMessage } from "../../../commons/Toast/ErrorMessage";
import {
  downloadListingSuperAdmin,
  getDocumentRaSuperAdmin,
} from "../../../Services/GNA/AlterApi";
import { getRequistionAdminConsumerRaListing } from "../../../Services/GNA/GetData";
import Styles from "./CompanyDetails.module.css";

const PAGE_SIZE_OPTIONS = [10, 20, 50];

const ListingTable = ({ type, entityRole, data, setListingView }) => {
  const [viewType, setViewType] = useState({ status: false, data: null });
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const handleGetReq = async ({ ele, id, bidStage, consumerId }) => {
    let res = await getRequistionAdminConsumerRaListing({
      id,
      bidStage,
      consumerId,
    });
    if (res?.data?.code === 200) {
      setViewType({ status: true, data: { ...ele, slots: res?.data?.data } });
    }
  };

  // ── Search filter ──────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter((ele) => {
      const searchable = [
        ele?.name,
        ele?.tenderNumber,
        ele?.auctionNumber,
        ele?.status,
        ele?.tenderStatus,
        ele?.auctionType,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return searchable.includes(q);
    });
  }, [data, search]);

  // Reset to page 0 on search change
  const handleSearch = (value) => {
    setSearch(value);
    setPage(0);
  };

  const totalPages = Math.ceil(filtered.length / pageSize);
  const paginated = filtered.slice(page * pageSize, page * pageSize + pageSize);

  // ── Empty state ────────────────────────────────────────────────────────────
  if (!data || data.length === 0) {
    return (
      <div className={Styles.listingEmpty}>
        No Listing(s) / Tender(s) available
      </div>
    );
  }

  // ── Column definitions ─────────────────────────────────────────────────────
  let headers;
  let renderRow;

  if (type === "bilateral") {
    headers = [
      { label: "Listing Number", flex: "2" },
      { label: "Requisition", flex: "1" },
      { label: "Listing Exp. Date & Time", flex: "2" },
      { label: "Contract Exp. Date & Time", flex: "2" },
      { label: "Status", flex: "1.2" },
      { label: "Download", flex: "0.6", center: true },
      { label: "Details", flex: "0.6", center: true },
    ];
    renderRow = (ele) => (
      <>
        <div style={{ flex: "2" }} className={Styles.ltCell}>
          {ele?.name || "—"}
        </div>
        <div
          style={{ flex: "1" }}
          className={`${Styles.ltCell} ${Styles.ltLink}`}
          onClick={() => setViewType({ status: true, data: ele })}
        >
          View
        </div>
        <div style={{ flex: "2" }} className={Styles.ltCell}>
          {formatDateType(ele?.listingExpDate)}: {ele?.listingExpTime}
        </div>
        <div style={{ flex: "2" }} className={Styles.ltCell}>
          {formatDateType(ele?.termsExpDate)}: {ele?.termsExpTime}
        </div>
        <div style={{ flex: "1.2" }} className={Styles.ltCell}>
          <span
            className={`${Styles.statusPill} ${Styles[`status_${ele?.status?.toLowerCase()}`] || ""}`}
          >
            {ele?.status === "TERMS_ACCEPTED" ? "TERMS" : ele?.status}
          </span>
        </div>
      </>
    );
  } else if (type === "ra" || type === "rfq") {
    const getInitiator = (ele) => {
      if (entityRole === "consumer" && ele?.productType === "BSRA") return "Self";
      if (entityRole === "generator" && ele?.productType === "BSFA") return "Self";
      return "-";
    };

    headers = [
      { label: "Tender Number", flex: "1.8" },
      ...(type === "ra" ? [{ label: "Auction Number", flex: "1.5" }] : []),
      { label: "Requisition", flex: "0.9", center: true },
      { label: "Tender Duration", flex: "2.5" },
      { label: "Tender Type", flex: "1", center: true },
      { label: "Initiator", flex: "0.8", center: true },
      { label: "Status", flex: "1.2", center: true },
      { label: "Download", flex: "0.6", center: true },
      { label: "Details", flex: "0.6", center: true },
    ];
    renderRow = (ele) => (
      <>
        <div style={{ flex: "1.8" }} className={Styles.ltCell}>
          {ele?.tenderNumber || "—"}
        </div>
        {type === "ra" && (
          <div style={{ flex: "1.5" }} className={Styles.ltCell}>
            {ele?.auctionNumber || "—"}
          </div>
        )}
        <div
          style={{ flex: "0.9" }}
          className={`${Styles.ltCell} ${Styles.ltLink} ${Styles.ltCenter}`}
          onClick={() =>
            handleGetReq({
              ele,
              id: ele?.id,
              consumerId: ele?.consumerId,
              bidStage: "AUCTION",
            })
          }
        >
          View
        </div>
        <div style={{ flex: "2.5" }} className={Styles.ltCell}>
          {formatDateTimeView(ele?.tenderStartDateTime)} →{" "}
          {formatDateTimeView(ele?.tenderCloseDateTime)}
        </div>
        <div
          style={{ flex: "1" }}
          className={`${Styles.ltCell} ${Styles.ltCenter}`}
        >
          {ele?.auctionType === "OPEN" ? "Interstate" : "Intrastate"}
        </div>
        <div
          style={{ flex: "0.8" }}
          className={`${Styles.ltCell} ${Styles.ltCenter}`}
        >
          {getInitiator(ele)}
        </div>
        <div
          style={{ flex: "1.2" }}
          className={`${Styles.ltCell} ${Styles.ltCenter}`}
        >
          <span
            className={`${Styles.statusPill} ${Styles[`status_${ele?.tenderStatus?.toLowerCase()}`] || ""}`}
          >
            {ele?.tenderStatus}
          </span>
        </div>
      </>
    );
  } else {
    return <div>Invalid listing type.</div>;
  }

  const handleDownload = async (
    id,
    userId,
    companyId,
    consumerId,
    generatorId,
  ) => {
    const payload = { id, companyId, userId };
    if (consumerId != null) payload.consumerId = consumerId;
    if (generatorId != null) payload.generatorId = generatorId;

    const response =
      type === "bilateral"
        ? await downloadListingSuperAdmin(payload)
        : await getDocumentRaSuperAdmin(payload);

    const blob = response?.data;
    if (!blob) return;
    const url = window.URL.createObjectURL(
      new Blob([blob], { type: "application/pdf" }),
    );
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      type === "bilateral" ? `listing_${id}.pdf` : `tender_${id}.pdf`,
    );
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    SuccessMessage("Downloaded successfully!");
  };

  const canView = (ele) =>
    ele?.tenderStatus === "TERMS" ||
    ele?.status === "TERMS_ACCEPTED" ||
    ele?.status === "SCHEDULED" ||
    ele?.status === "ONGOING";

  return (
    <>
      {viewType.status && (
        <ViewInjectDrawal
          open={viewType.status}
          handleClose={() => setViewType({ status: false, data: null })}
          data={[viewType.data]}
          activeListingId={viewType.data.id}
        />
      )}

      {/* ── Toolbar: search + page size ── */}
      <div className={Styles.ltToolbar}>
        <div className={Styles.ltSearchBox}>
          <SearchIcon
            style={{ fontSize: "15px", color: "#a0aec0", flexShrink: 0 }}
          />
          <input
            className={Styles.ltSearchInput}
            type="text"
            placeholder={
              type === "bilateral"
                ? "Search listing number, status…"
                : "Search tender number, status…"
            }
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
          />
          {search && (
            <button className={Styles.ltClear} onClick={() => handleSearch("")}>
              ✕
            </button>
          )}
        </div>
        <div className={Styles.ltToolbarRight}>
          <span className={Styles.ltResultCount}>
            {filtered.length} result{filtered.length !== 1 ? "s" : ""}
          </span>
          <select
            className={Styles.ltPageSizeSelect}
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(0);
            }}
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n} / page
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* ── Table header ── */}
      <div className={Styles.ltHead}>
        {headers.map((h, i) => (
          <div
            key={i}
            style={{ flex: h.flex }}
            className={`${Styles.ltHeadCell} ${h.center ? Styles.ltCenter : ""}`}
          >
            {h.label}
          </div>
        ))}
      </div>

      {/* ── Rows ── */}
      {paginated.length === 0 ? (
        <div className={Styles.ltNoResult}>
          No results for "<strong>{search}</strong>"
        </div>
      ) : (
        paginated.map((ele, index) => (
          <div key={index} className={Styles.ltRow}>
            {renderRow(ele)}
            {/* Download */}
            <div
              style={{ flex: "0.6" }}
              className={`${Styles.ltCell} ${Styles.ltCenter}`}
            >
              <button
                className={Styles.ltIconBtn}
                title="Download"
                onClick={() =>
                  handleDownload(
                    ele?.id,
                    ele?.userId,
                    ele?.companyId,
                    ele?.consumerId,
                    ele?.generatorId,
                  )
                }
              >
                <DownloadIcon style={{ fontSize: "16px" }} />
              </button>
            </div>
            {/* Details */}
            <div
              style={{ flex: "0.6" }}
              className={`${Styles.ltCell} ${Styles.ltCenter}`}
            >
              <button
                className={`${Styles.ltIconBtn} ${!canView(ele) ? Styles.ltIconBtnDisabled : ""}`}
                title="View details"
                disabled={!canView(ele)}
                onClick={() =>
                  canView(ele) && setListingView({ open: true, data: ele })
                }
              >
                <RemoveRedEyeIcon style={{ fontSize: "16px" }} />
              </button>
            </div>
          </div>
        ))
      )}

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <div className={Styles.ltPagination}>
          <span className={Styles.ltPageInfo}>
            {page * pageSize + 1}–
            {Math.min((page + 1) * pageSize, filtered.length)} of{" "}
            {filtered.length}
          </span>
          <div className={Styles.ltPageBtns}>
            <button
              className={Styles.ltPageBtn}
              disabled={page === 0}
              onClick={() => setPage((p) => p - 1)}
            >
              ‹
            </button>
            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
              const start = Math.max(0, Math.min(page - 3, totalPages - 7));
              const pageNum = start + i;
              return (
                <button
                  key={pageNum}
                  className={`${Styles.ltPageBtn} ${pageNum === page ? Styles.ltPageBtnActive : ""}`}
                  onClick={() => setPage(pageNum)}
                >
                  {pageNum + 1}
                </button>
              );
            })}
            <button
              className={Styles.ltPageBtn}
              disabled={page >= totalPages - 1}
              onClick={() => setPage((p) => p + 1)}
            >
              ›
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ListingTable;
